import React, { useCallback, useState } from 'react';
import {
  createTheme,
  CssBaseline,
  Theme as MuiTheme,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material';
import { theme as baseTheme } from 'redesign';
import { ProviderFactory } from 'shared/providers/provider-factory';
import { buildDarkAccentScale, buildLightAccentScale } from 'shared/utils/color-utils';
import { z } from 'zod';

const companyThemeSchema = z.object({
  primaryColor: z.string().min(1),
  secondaryColor: z.string().min(1),
  primaryAccent: z.string().min(1),
  secondaryAccent: z.string().min(1),
});

export type CompanyTheme = z.infer<typeof companyThemeSchema>;

interface ThemeContextValue {
  setTheme: (theme: CompanyTheme | null | undefined) => void;
}

export const [ThemeContextProvider, useThemeContext] = ProviderFactory<ThemeContextValue>(
  'useThemeContext must be used within ThemeProvider'
);

function applyCompanyTheme(payload: CompanyTheme | null | undefined): MuiTheme {
  const result = companyThemeSchema.safeParse(payload);
  if (!result.success) {
    return baseTheme;
  }
  const theme = result.data;
  return createTheme(baseTheme, {
    palette: {
      lwPrimaryAccentLight: buildLightAccentScale(theme.primaryAccent),
      lwPrimaryAccentDark: buildDarkAccentScale(theme.primaryAccent),
      lwSecondaryAccentLight: buildLightAccentScale(theme.secondaryAccent),
      lwSecondaryAccentDark: buildDarkAccentScale(theme.secondaryAccent),
      primary: {
        main: theme.primaryColor,
      },
      secondary: {
        main: theme.secondaryColor,
      },
    },
  });
}

export const ThemeProvider = ({ children }: React.PropsWithChildren<unknown>) => {
  const [theme, setThemeState] = useState<MuiTheme>(() => baseTheme);

  const setTheme = useCallback((payload: CompanyTheme | null | undefined) => {
    setThemeState(applyCompanyTheme(payload));
  }, []);

  return (
    <ThemeContextProvider value={{ setTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContextProvider>
  );
};
